import { useEffect, useMemo, useState } from 'react';
import { Trash2, ChevronDown, ChevronUp, FileWarning } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Textarea,
  Switch,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useErrorPages,
  useUpdateErrorPages,
  useDeleteErrorPages,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { configTone, configLabel } from '../shared';
import { CdnAspectDrift } from '../_shared/VendorDrift';

const HTTP_STATUS_CODES = [
  '400', '401', '403', '404', '405', '406', '408', '410', '413', '415', '429', '451',
  '500', '502', '503', '504', '520', '521', '522', '523', '524',
];

// `{code}` stays literal — it's substituted at render/preview time.
const TEMPLATE_HTML =
  `<!DOCTYPE html>\n<html>\n<head><title>Error {code}</title></head>\n<body style="font-family:sans-serif;text-align:center;padding:60px">\n  <h1>{code}</h1>\n  <p>An error occurred while processing your request.</p>\n</body>\n</html>`;

export function ErrorPagesTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data, isLoading } = useErrorPages(domainId);

  const [enabled, setEnabled] = useState(false);
  const [pages, setPages] = useState<Record<string, string>>({});
  const [snapshot, setSnapshot] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [addCode, setAddCode] = useState('');
  const [pendingRemoveCode, setPendingRemoveCode] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setPages(data.pages);
    setSnapshot(JSON.stringify({ enabled: data.enabled, pages: data.pages }));
  }, [data]);

  const update = useUpdateErrorPages(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'error_page', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Error pages saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save error pages', description: errorMessage(e, 'Could not save error pages') }),
  });
  const remove = useDeleteErrorPages(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'error_page', 'delete');
      show({ tone: 'success', title: 'Error pages reset' });
      setPages({});
      setEnabled(false);
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not reset error pages', description: errorMessage(e, 'Could not reset error pages') }),
  });
  const busy = update.isPending || remove.isPending;

  const dirty = useMemo(() => JSON.stringify({ enabled, pages }) !== snapshot, [enabled, pages, snapshot]);
  const isConfigured = !!data;
  const configuredCodes = Object.keys(pages).sort();
  const availableCodes = HTTP_STATUS_CODES.filter((c) => !configuredCodes.includes(c));

  const addPage = (code: string) => {
    if (!code || pages[code] !== undefined) return;
    setPages((p) => ({ ...p, [code]: TEMPLATE_HTML }));
    setExpanded((s) => new Set(s).add(code));
    setAddCode('');
  };
  const removePage = (code: string) =>
    setPages((p) => {
      const next = { ...p };
      delete next[code];
      return next;
    });

  if (isLoading) {
    return (
      <Section title="Error Pages">
        <Skeleton style={{ height: 220 }} />
      </Section>
    );
  }

  return (
    <Section
      title="Error Pages"
      description={`Custom error pages served from the edge for ${domain}.`}
      actions={<StatusPill tone={configTone(enabled)}>{enabled ? 'Enabled' : configLabel(isConfigured)}</StatusPill>}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <CdnAspectDrift domainId={domainId} path="error-pages" />
        <Card>
          <CardHeader>
            <CardTitle>Custom error pages</CardTitle>
            <CardDescription>Serve your own HTML for specific HTTP status codes. The token {'{code}'} is substituted.</CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <SettingRow
              title="Enabled"
              description="Master switch for custom error pages on this domain."
              control={<Switch checked={enabled} onCheckedChange={setEnabled} />}
            />

            {enabled && (
              <>
                <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'flex-end', maxWidth: 320 }}>
                  <div style={{ flex: 1 }}>
                    <Select value={addCode} onValueChange={addPage}>
                      <SelectTrigger>
                        <SelectValue placeholder="Add a status code…" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCodes.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {configuredCodes.length === 0 ? (
                  <EmptyState size="sm" icon={<FileWarning size={20} />} title="No error pages yet" description="Pick a status code above to add a custom page." />
                ) : (
                  <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                    {configuredCodes.map((code) => (
                      <ErrorPageItem
                        key={code}
                        code={code}
                        html={pages[code]}
                        expanded={expanded.has(code)}
                        onToggle={() =>
                          setExpanded((s) => {
                            const next = new Set(s);
                            next.has(code) ? next.delete(code) : next.add(code);
                            return next;
                          })
                        }
                        onChange={(html) => setPages((p) => ({ ...p, [code]: html }))}
                        onRemove={() => setPendingRemoveCode(code)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>

        <ActionBar
          start={
            isConfigured ? (
              <Button variant="secondary" disabled={busy} onClick={() => remove.mutate()}>
                Remove all
              </Button>
            ) : undefined
          }
          end={
            <>
              {dirty && (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => {
                    if (data) {
                      setEnabled(data.enabled);
                      setPages(data.pages);
                    }
                  }}
                >
                  Discard
                </Button>
              )}
              <Button disabled={!dirty || busy} onClick={() => update.mutate({ enabled, pages })}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          }
        />
      </div>

      <ConfirmDialog
        open={!!pendingRemoveCode}
        onOpenChange={(o) => !o && setPendingRemoveCode(null)}
        title="Remove error page?"
        description={`Remove the custom error page for status ${pendingRemoveCode}? Save to persist.`}
        confirmLabel="Remove"
        destructive
        onConfirm={() => {
          if (pendingRemoveCode) removePage(pendingRemoveCode);
          setPendingRemoveCode(null);
        }}
      />
    </Section>
  );
}

function ErrorPageItem({
  code,
  html,
  expanded,
  onToggle,
  onChange,
  onRemove,
}: {
  code: string;
  html: string;
  expanded: boolean;
  onToggle: () => void;
  onChange: (html: string) => void;
  onRemove: () => void;
}) {
  const preview = (html || '').replace(/\{code\}/g, code);
  return (
    <div style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-2) var(--vn-space-3)' }}>
        <span style={{ fontWeight: 'var(--vn-font-weight-semibold)', fontSize: 'var(--vn-font-size-lg)', flex: 1 }}>{code} error page</span>
        <IconButton variant="ghost" size="sm" aria-label="Remove page" onClick={onRemove}>
          <Trash2 size={15} />
        </IconButton>
        <IconButton variant="ghost" size="sm" aria-label="Toggle page" onClick={onToggle}>
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </IconButton>
      </div>
      {expanded && (
        <div
          style={{
            borderTop: '1px solid var(--vn-border-default)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 0,
          }}
        >
          <div style={{ padding: 'var(--vn-space-3)', borderRight: '1px solid var(--vn-border-default)' }}>
            <Textarea
              rows={12}
              value={html}
              onChange={(e) => onChange(e.target.value)}
              style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)', width: '100%' }}
            />
            <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 'var(--vn-space-1)' }}>Available token: {'{code}'}</div>
          </div>
          <div style={{ padding: 'var(--vn-space-3)' }}>
            <iframe
              title={`Preview ${code}`}
              srcDoc={preview}
              style={{ width: '100%', height: 280, border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-sm)', background: 'var(--vn-bg-surface)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
